/**
 * VerticalLayout - Vertical stacking layout for photo collections
 *
 * Displays photos in a vertical stack with full-width images.
 * Images maintain aspect ratios and consume 100% of available container width.
 * Designed for vertical scrolling feed experiences with optional critique display.
 *
 * Note: Zoom is ignored in this layout as images always fill container width.
 *
 * @module PhotoCollection2/layouts/VerticalLayout
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhotoItem2 } from '../../PhotoItem2';
import { PhotoContextMenu } from '../../PhotoContextMenu';
import { cn } from '@/lib/utils';
import { Text } from '../../atoms/Text';
import { useAuth } from '@/services/auth';
import type { PhotoCollection2LayoutProps, ReorderContext } from '../PhotoCollection2.types';
import type { PhotoItem2ViewModel } from '../../PhotoItem2/PhotoItem2.types';

/**
 * Props for SortableVerticalItem wrapper
 */
interface SortableVerticalItemProps {
  photo: PhotoItem2ViewModel;
  itemTemplate: PhotoCollection2LayoutProps['itemTemplate'];
  selectionMode: PhotoCollection2LayoutProps['selectionMode'];
  isSelected: boolean;
  zoom: number;
  onItemClick?: (photoId: string, event?: React.MouseEvent) => void;
  onCritiqueClick?: (photoId: string, critiqueId: string) => void;
  reorderContext?: ReorderContext;
  hasSelection: boolean;
  /** Current user's ID for ownership check (Feature 081: photo-versioning) */
  currentUserId?: string;
}

/**
 * Wrapper component that makes the entire photo item draggable in vertical layout
 */
function SortableVerticalItem({
  photo,
  itemTemplate,
  selectionMode,
  isSelected,
  zoom,
  onItemClick,
  onCritiqueClick,
  reorderContext,
  hasSelection,
  currentUserId,
}: SortableVerticalItemProps) {
  const isDragEnabled = reorderContext?.enabled && !hasSelection;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.photoData.id,
    disabled: !isDragEnabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    cursor: isDragEnabled ? (isDragging ? 'grabbing' : 'grab') : undefined,
    // CSS content-visibility optimization
    contentVisibility: 'auto',
    containIntrinsicSize: 'auto 500px',
  };

  const dragProps = isDragEnabled
    ? {
        ...attributes,
        ...listeners,
        'aria-roledescription': 'sortable',
        'aria-describedby': 'dnd-instructions',
      }
    : {};

  // Feature 081: photo-versioning - Check if current user owns this photo
  const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      data-photo-id={photo.photoData.id}
      className="w-full flex-shrink-0 p-2 relative"
      style={style}
    >
      <PhotoContextMenu
        photoId={photo.photoData.id}
        isOwner={isOwner}
      >
        <PhotoItem2
          data={photo}
          template={itemTemplate}
          isSelected={selectionMode !== 'none' && isSelected}
          selectable={selectionMode !== 'none'}
          zoom={zoom}
          onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
          onCritiqueClick={onCritiqueClick}
        />
      </PhotoContextMenu>
    </div>
  );
}

/**
 * VerticalLayout component
 *
 * Renders photos in a vertical stack with:
 * - Full container width for all photos
 * - Variable heights calculated from aspect ratios (natural height preservation)
 * - Vertical scrolling (managed by browser's native scroll)
 * - No gaps between items (seamless feed)
 * - Aspect ratio preservation (no cropping or distortion)
 * - CSS content-visibility optimization for performance with many photos
 *
 * Zoom behavior: Ignored (zoom is not meaningful for full-width vertical layout)
 * Selection: Fully supported (delegates to PhotoItem2)
 *
 * @example
 * ```typescript
 * <VerticalLayout
 *   items={photos}
 *   itemTemplate="classic-feed"
 *   selectionMode="multiple"
 *   selectedIds={['photo-1', 'photo-2']}
 *   onItemClick={handleClick}
 * />
 * ```
 */
export function VerticalLayout({
  items,
  itemTemplate,
  selectionMode,
  selectedIds,
  zoom = 1.0, // Ignored for vertical layout (documented)
  onItemClick,
  onCritiqueClick,
  reorderContext,
  className,
}: PhotoCollection2LayoutProps) {
  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Empty state
  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-center', className)}>
        <Text variant="muted">No photos in this collection</Text>
      </div>
    );
  }

  // Check if any items are selected (for disabling drag during selection)
  const hasSelection = selectedIds.length > 0;

  // Note: SortableContext is provided by PhotoCollection2, not here
  // This prevents double-wrapping which could cause drag-and-drop issues
  return (
    <div
      className={cn('flex flex-col w-full', className)}
    >
      {items.map((photo) => {
        // Feature 081: photo-versioning - Check if current user owns this photo
        const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

        return reorderContext?.enabled ? (
          <SortableVerticalItem
            key={photo.photoData.id}
            photo={photo}
            itemTemplate={itemTemplate}
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(photo.photoData.id)}
            zoom={zoom}
            onItemClick={onItemClick}
            onCritiqueClick={onCritiqueClick}
            reorderContext={reorderContext}
            hasSelection={hasSelection}
            currentUserId={currentUserId}
          />
        ) : (
          <div
            key={photo.photoData.id}
            data-photo-id={photo.photoData.id}
            className="w-full flex-shrink-0 p-2 relative"
            style={{
              // CSS content-visibility optimization
              // Browser automatically skips rendering off-screen items
              // Improves scroll performance with many photos (50+)
              contentVisibility: 'auto',
              // Estimated intrinsic height for better scroll bar sizing
              // Assumes average aspect ratio of 3:2 landscape (500px height at full width ~750px)
              containIntrinsicSize: 'auto 500px',
            }}
          >
            <PhotoContextMenu
              photoId={photo.photoData.id}
              isOwner={isOwner}
            >
              <PhotoItem2
                data={photo}
                template={itemTemplate}
                isSelected={selectionMode !== 'none' && selectedIds.includes(photo.photoData.id)}
                selectable={selectionMode !== 'none'}
                zoom={zoom}
                onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
                onCritiqueClick={onCritiqueClick}
              />
            </PhotoContextMenu>
          </div>
        );
      })}
    </div>
  );
}
